import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance with base URL
const api = axios.create({
  baseURL: apiUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include token in all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // If we get a 401/403, the token is likely invalid
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Clear invalid token and user data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('currentAccountType');
      
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Create the Auth Context
const AuthContext = createContext();

// Custom hook to use the Auth Context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  // Initialize state with localStorage values or defaults
  const [currentAccountType, setCurrentAccountType] = useState(() => {
    const saved = localStorage.getItem('currentAccountType');
    return saved || 'admin';
  });

  const [availableAccountTypes] = useState(['admin', 'funeral_director', 'designer', 'staff']);

  // Auth state
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });

  const [token, setToken] = useState(() => {
    return localStorage.getItem('token') || null;
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isValidatingToken, setIsValidatingToken] = useState(false);
  const [registrationPassare, setRegistrationPassare] = useState(() => {
    const saved = sessionStorage.getItem('registrationPassare');
    return saved ? JSON.parse(saved) : null;
  });

  const isAuthenticated = !!user && !!token;

  // Validate token on app load
  useEffect(() => {
    const initializeAuth = async () => {
      // Only validate if we have a token and user data
      if (token && user) {
        await validateToken();
      }
    };
    
    initializeAuth();
  }, []); // Run once on mount

  // Save account type to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('currentAccountType', currentAccountType);
  }, [currentAccountType]);

  const switchAccountType = (type) => {
    setCurrentAccountType(type);
    console.log(`Switched to account type: ${type}`);
  };

  const validateToken = async () => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (!storedToken || !storedUser) {
      // No token or user data, ensure clean state
      logout();
      return false;
    }

    try {
      setIsValidatingToken(true);
      
      // Try to get current user profile to validate token
      const response = await api.get('/api/users/me');
      const currentUser = response.data;
      
      // Check if the user still exists and token is valid
      if (currentUser && currentUser.id) {
        // Token is valid, update user data if needed
        setUser(currentUser);
        localStorage.setItem('user', JSON.stringify(currentUser));
        return true;
      } else {
        // User doesn't exist or token is invalid
        logout();
        return false;
      }
    } catch (error) {
      console.error('Token validation failed:', error);
      
      // If it's a 401/403, token is invalid
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('Token is invalid, logging out');
        logout();
        return false;
      }
      
      // For other errors (network, etc.), keep the user logged in
      // but log the error for debugging
      console.warn('Token validation failed due to network error, keeping user logged in');
      return true;
    } finally {
      setIsValidatingToken(false);
    }
  };

  const login = async (email, password) => {
    try {
      setIsLoading(true);
      const response = await api.post('/api/auth/login', { email, password });
      const { user: userData, token: authToken } = response.data;
      
      setUser(userData);
      setToken(authToken);
      localStorage.setItem('token', authToken);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Set initial account type based on user's primary type
      const initialAccountType = userData?.type || 'admin';
      setCurrentAccountType(initialAccountType);
      localStorage.setItem('currentAccountType', initialAccountType);
      setRegistrationPassare(null);
      sessionStorage.removeItem('registrationPassare');
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setIsLoading(true);
      const response = await api.post('/api/auth/register', userData);
      const { user: newUser, token: authToken, passare } = response.data;
      
      setUser(newUser);
      setToken(authToken);
      localStorage.setItem('token', authToken);
      localStorage.setItem('user', JSON.stringify(newUser));
      if (passare) {
        setRegistrationPassare(passare);
        sessionStorage.setItem('registrationPassare', JSON.stringify(passare));
      } else {
        setRegistrationPassare(null);
        sessionStorage.removeItem('registrationPassare');
      }
      
      // Set initial account type
      const initialAccountType = newUser?.type || 'admin';
      setCurrentAccountType(initialAccountType);
      localStorage.setItem('currentAccountType', initialAccountType);
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setCurrentAccountType(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('currentAccountType');
    setRegistrationPassare(null);
    sessionStorage.removeItem('registrationPassare');
  };

  const clearRegistrationPassare = () => {
    setRegistrationPassare(null);
    sessionStorage.removeItem('registrationPassare');
  };

  const sendPasswordReset = async (email) => {
    try {
      setIsLoading(true);
      const response = await api.post('/api/users/forgot-password', { email });
      return response.data;
    } catch (error) {
      console.error('Forgot password error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getPaymentMethods = async () => {
    try {
      const response = await api.get('/api/users/me/paymentMethods');
      return response.data;
    } catch (error) {
      console.error('Get payment methods error:', error);
      // Return empty array if endpoint doesn't exist yet
      if (error.response?.status === 404) {
        return [];
      }
      throw error;
    }
  };

  const value = {
    user,
    token,
    currentAccountType,
    availableAccountTypes,
    switchAccountType,
    getPaymentMethods,
    login,
    register,
    logout,
    sendPasswordReset,
    validateToken,
    isLoading,
    isValidatingToken,
    isAuthenticated,
    registrationPassare,
    clearRegistrationPassare,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
